import { useEffect, useId, useRef, useState } from "react";
import { Button, InputText } from "@/deps/ui-library";
import type { Party } from "@/api/types";
import { useTaxWords } from "@/app/session";

/**
 * Picks a customer or supplier by name, phone or PAN.
 *
 * A combobox rather than a select: a book with four hundred customers is unusable as a dropdown,
 * and people search by whatever they happen to have — a phone number off a cheque, a PAN off a
 * bill.
 *
 * Built from RDS parts (the label style, `InputText.Input`, `Button.IconButton`) around its own
 * listbox rather than on RDS `ComboBox`. The ComboBox selects on click, fills the input with the
 * chosen label, and searches only once something is typed. This one searches the moment it is
 * focused (an empty query lists everyone), selects on mousedown so the input's blur cannot close
 * the list first, drops answers that arrive out of order, and shows the chosen party with its PAN
 * beside the name — the reason the control exists.
 */
export function PartyCombobox({
  label,
  value,
  onChange,
  search,
  required,
  hideLabel,
  placeholder,
}: {
  label: string;
  value: Party | null;
  onChange: (party: Party | null) => void;
  search: (query: string) => Promise<Party[]>;
  required?: boolean;
  /** Keeps the label for a screen reader, in a table whose column header says it for everyone else. */
  hideLabel?: boolean;
  placeholder?: string;
}) {
  const say = useTaxWords();
  const id = useId();
  const listId = `${id}-list`;
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Party[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    // Debounced: a keystroke per request turns "Bhatbhateni" into eleven of them, and the answers
    // can arrive out of order.
    const timer = setTimeout(() => {
      void search(query).then((found) => {
        if (!cancelled) {
          setResults(found);
          setActive(0);
        }
      });
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, open, search]);

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, []);

  const choose = (party: Party) => {
    onChange(party);
    setQuery("");
    setOpen(false);
  };

  return (
    <div className="flex w-full flex-col gap-0.5" ref={box}>
      <label htmlFor={id} className={hideLabel ? "sr-only" : "text-foreground text-sm font-medium"}>
        {label}
        {required ? (
          <span className="text-destructive ml-0.5" aria-hidden="true">
            *
          </span>
        ) : null}
        {required ? <span className="sr-only"> (required)</span> : null}
      </label>

      {value ? (
        <div className="border-input-border bg-input-background flex h-8 items-center gap-2 rounded-md border pr-0.5 pl-2.5">
          <span className="text-foreground min-w-0 flex-1 truncate text-sm">{value.name}</span>
          {value.pan ? (
            <span className="text-muted-foreground num text-xs">{value.pan}</span>
          ) : null}
          <Button.IconButton
            icon="X"
            variant="ghost"
            colorScheme="default"
            size="xs"
            onClick={() => onChange(null)}
            aria-label={`Clear ${label}`}
          />
        </div>
      ) : (
        <div className="relative">
          <InputText.Input
            id={id}
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={open && results[active] ? `${listId}-${active}` : undefined}
            value={query}
            placeholder={placeholder ?? say("Search by name, phone or PAN")}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                setActive((a) => Math.min(a + 1, results.length - 1));
              }
              if (event.key === "ArrowUp") {
                event.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              }
              if (event.key === "Enter" && results[active]) {
                event.preventDefault();
                choose(results[active]);
              }
              if (event.key === "Escape") setOpen(false);
            }}
            config={{ size: "sm" }}
          />

          {/* A div rather than a ul: the roles carry the semantics here, and a list element
              given an interactive role is a mismatch a screen reader has to reconcile.

              prefer-tag-over-role wants a native select element. A select cannot be typed into, cannot be
              filtered, and cannot show a PAN beside a name — which is the entire reason this
              control exists. The WAI-ARIA combobox pattern specifies exactly this markup. */}
          {open ? (
            <div
              id={listId}
              role="listbox"
              aria-label={label}
              className="border-border bg-popover absolute z-floating mt-1 max-h-56 w-full overflow-auto rounded-md border p-1 shadow-md"
            >
              {results.length === 0 ? (
                // An empty result says what to do next rather than showing nothing at all.
                <p className="text-muted-foreground px-2 py-2 text-xs">
                  {query.length === 0 ? "Start typing to search." : `No match for "${query}".`}
                </p>
              ) : (
                results.map((party, index) => (
                  <div
                    key={party.id}
                    id={`${listId}-${index}`}
                    role="option"
                    // Focusable programmatically but not in the tab order: focus stays on the
                    // input and moves through the list via aria-activedescendant, which is what
                    // lets somebody keep typing while arrowing down.
                    tabIndex={-1}
                    aria-selected={index === active}
                    onMouseDown={(event) => {
                      // mousedown, not click: the input's blur would close the list first.
                      event.preventDefault();
                      choose(party);
                    }}
                    className={[
                      "text-foreground flex cursor-pointer items-baseline gap-2 rounded-sm px-2 py-1.5 text-sm",
                      index === active ? "bg-muted" : "hover:bg-muted",
                    ].join(" ")}
                  >
                    <span className="min-w-0 flex-1 truncate">{party.name}</span>
                    {party.pan ? (
                      <span className="text-muted-foreground num text-xs">{party.pan}</span>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
